import { useEffect, useState } from "react";
import axios from "axios";
import { Award, FileText } from "lucide-react";
import { Reveal, SectionHead, PageHero, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Certifications = () => {
  usePageMeta(
    "Certifications & Approvals — G. R. Engineering Private Limited",
    "Certifications and approvals held by G. R. Engineering Private Limited — ASME, ISO and quality approvals for pressure equipment fabrication."
  );
  const [certs, setCerts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/certifications`).then((r) => setCerts(r.data)).catch(() => setCerts([]));
  }, []);

  return (
    <>
      <PageHero
        img={IMG("exchanger-a")}
        eyebrow="Certifications & Approvals"
        title="Certified engineering, on record"
        sub="Every certification and approval listed here is documented and verifiable."
      />
      <section className="bg-slate-50" data-testid="certifications-section">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="Our Credentials" title="Certifications & Approvals" />
          {certs === null ? (
            <p className="mt-12 text-sm text-slate-400">Loading…</p>
          ) : certs.length === 0 ? (
            <div className="mt-12 bg-white border border-slate-200 p-10 text-center max-w-xl" data-testid="certifications-empty">
              <Award size={28} className="mx-auto text-slate-300" />
              <p className="mt-4 text-sm text-slate-600">Certification records are currently being updated. Please contact us for documentation.</p>
            </div>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
              {certs.map((c, i) => (
                <Reveal key={c.id} delay={(i % 3) * 0.07}>
                  <article className="bg-white p-8 h-full flex flex-col card-lift border border-transparent" data-testid={`cert-card-${c.id}`}>
                    <Award size={24} className="text-[#C5221F]" strokeWidth={1.75} />
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900 leading-snug">{c.title}</h3>
                    {c.issuing_authority && <p className="mt-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500">{c.issuing_authority}</p>}
                    <dl className="mt-4 space-y-1.5 text-[13px] text-slate-600">
                      {c.cert_number && <p><span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400 block">Certificate No.</span>{c.cert_number}</p>}
                      {(c.issue_date || c.expiry_date) && (
                        <p><span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400 block">Validity</span>
                        {c.issue_date && `Issued ${c.issue_date}`}{c.issue_date && c.expiry_date && " · "}{c.expiry_date && `Valid through ${c.expiry_date}`}</p>
                      )}
                    </dl>
                    {c.description && <p className="mt-3 text-[13px] leading-relaxed text-slate-600 flex-1">{c.description}</p>}
                    {c.doc_url && (
                      <a href={c.doc_url} target="_blank" rel="noopener noreferrer" data-testid={`cert-view-${c.id}`}
                        className="group mt-6 inline-flex items-center gap-2.5 self-start bg-[#0A1128] hover:bg-[#C5221F] text-white font-display font-semibold px-5 py-3 text-xs uppercase tracking-widest transition-colors duration-300">
                        <FileText size={14} /> View Certificate
                      </a>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default Certifications;
